import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(localizedFormat);

import { DashboardLayout } from '../../layouts';
import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Icons, parseToken, useLocalStorage, windowExists } from '@classity-edtech/ui-base';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { ChatBubbleOutline, CheckCircle } from '@mui/icons-material';
import { webPaths } from '../../routes/paths';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useRelatedUsersQuery } from '../../services/users';
import { useGetResponseFromCacheQuery } from '../../services/response';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

export interface HomeProps extends MuiBoxProps {}

export const Dashboard = ({ className }: HomeProps) => {
  const screens = useAppMediaQuery();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [, setUserFlow] = useLocalStorage('userFlow', '');
  const [showEmailNotification, setShowEmailNotification] = useState(true);
  const token =
    windowExists &&
    parseToken(
      (windowExists?.localStorage as { getItem: (key: string) => void })?.getItem('token'),
    );
  const { data: relatedUsers, isLoading: loadingRelatedUsers } = useRelatedUsersQuery({
    userId: token.user,
  });
  const { data: cacheResponse } = useGetResponseFromCacheQuery(
    { userId: token.user },
    { skip: !token.user },
  );

  const handleRegisterChild = () => {
    setUserFlow('assessment');
    navigate(`/${webPaths.onboardStudent}`);
  };

  const continueRegisteration = () => {
    navigate(`/${webPaths.studentEnrolment}`);
  };

  const toggleEmailNotitfication = () => {
    setShowEmailNotification(showEmailNotification => !showEmailNotification);
  };

  const uncompletedChild = () => {
    const fullName = cacheResponse?.[cacheResponse?.length - 1]?.find(
      response => response.label === 'full-name',
    );
    const meta = cacheResponse?.[cacheResponse?.length - 1]?.find(
      response => response.label === 'meta',
    );

    return {
      firstName:
        fullName?.value?.value?.[fullName?.value?.name?.findIndex(name => name === 'firstname')],
      lastname:
        fullName?.value?.value?.[fullName?.value?.name?.findIndex(name => name === 'lastname')],
      createdAt: meta?.value?.value?.[meta?.value?.name?.findIndex(name => name === 'createdAt')],
    };
  };

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  return (
    <>
      {loadingRelatedUsers ? (
        <Box
          className={`${className} loader`}
          sx={{
            display: 'flex',
            width: '100vw',
            height: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DashboardLayout>
          {!relatedUsers || relatedUsers?.length < 1 ? (
            <Stack
              sx={{
                maxWidth: '500px',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                height: { xs: 'calc(100vh - 165px)', md: 'auto' },
                mt: { xs: '-32px', md: '0' },
              }}
            >
              <Icons.GridIcon
                sx={{
                  width: '96px',
                  height: '96px',
                  mb: 4,
                  color: globalColours['$global-colour-palette-gray-200'],
                }}
              />
              <Typography variant='subtitle3' sx={{ textAlign: 'center' }}>
                {localisedMessage('dashboard.home.no.registered.child')}
              </Typography>
              <Typography variant='body3' sx={{ textAlign: 'center' }}>
                {localisedMessage('dashboard.home.no.registered.child.description')}
              </Typography>

              <Button
                onClick={handleRegisterChild}
                size='large'
                sx={{ width: '100% !important', mt: 2 }}
              >
                {localisedMessage('dashboard.home.register.child')}
              </Button>
            </Stack>
          ) : (
            <Stack sx={{ maxWidth: '600px', gap: 6 }}>
              {!user?.user?.emailVerified && showEmailNotification && (
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    gap: 1.2,
                    background: globalColours['$global-colour-background-overlay'],
                    borderRadius: '16px',
                    maxWidth: '500px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      aspectRatio: '1 / 1',
                      minWidth: '48px',
                      minHeight: '48px',
                      width: '48px',
                      height: '48px',
                      background: globalColours['$global-colour-sentiment-warning-default'],
                      borderRadius: '100%',
                    }}
                  >
                    <ChatBubbleOutline
                      sx={{
                        width: '18px',
                        height: '18px',
                      }}
                    />
                  </Box>
                  <Stack sx={{ py: 2, gap: 1.2 }}>
                    <Typography variant='body3'>
                      {localisedMessage('dashboard.home.verify.email.info')}
                    </Typography>
                    <Link to={`/${webPaths.verifyEmail}`}>
                      <Typography variant='caption1'>{localisedMessage('verify.email')}</Typography>
                    </Link>
                  </Stack>
                  <Icons.Cancel
                    sx={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    onClick={toggleEmailNotitfication}
                  />
                </Box>
              )}
              <Box>
                <Typography variant={screens.isDesktop ? 'subtitle3' : 'subtitle4'}>
                  {localisedMessage('registrations')}
                </Typography>
                <Divider sx={{ mb: 4 }} />
                {relatedUsers?.map(user => (
                  <Box
                    key={user.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          aspectRatio: '1 / 1',
                          minWidth: '48px',
                          minHeight: '48px',
                          width: '48px',
                          height: '48px',
                          background: globalColours['$global-colour-background-overlay'],
                          borderRadius: '100%',
                        }}
                      >
                        <Typography variant='body2SemiBold'>
                          {user?.profile?.firstname?.charAt(0)} {user?.profile?.lastname?.charAt(0)}
                        </Typography>
                      </Box>
                      <Stack sx={{ gap: 0.5 }}>
                        <Typography variant='body2SemiBold'>
                          {user?.profile?.firstname} {user?.profile?.lastname}
                        </Typography>
                        <Typography variant='body3'>
                          {localisedMessage('registered')} ·{' '}
                          {dayjs(user?.meta?.created).format('ll')}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack sx={{ gap: 0.5, alignItems: 'center' }}>
                      <Typography variant='body2SemiBold'>Active</Typography>
                      <CheckCircle
                        sx={{ color: globalColours['$global-colour-sentiment-positive-active'] }}
                      />
                    </Stack>
                  </Box>
                ))}
                {uncompletedChild().firstName && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      borderRadius: '10px',
                      p: 2,
                      '&:hover': {
                        background: globalColours['$global-colour-background-neutral-default'],
                      },
                    }}
                    onClick={continueRegisteration}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          aspectRatio: '1 / 1',
                          minWidth: '48px',
                          minHeight: '48px',
                          width: '48px',
                          height: '48px',
                          background: globalColours['$global-colour-background-overlay'],
                          borderRadius: '100%',
                        }}
                      >
                        <Typography variant='body2SemiBold'>
                          {uncompletedChild().firstName.charAt(0)}{' '}
                          {uncompletedChild().lastname.charAt(0)}
                        </Typography>
                      </Box>
                      <Stack sx={{ gap: 0.5 }}>
                        <Typography variant='body2SemiBold'>
                          {uncompletedChild().firstName} {uncompletedChild().lastname}
                        </Typography>
                        <Typography variant='body3'>
                          {localisedMessage('in.progress')} ·{' '}
                          {dayjs(uncompletedChild().createdAt).format('ll')}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack sx={{ gap: 0.5, alignItems: 'flex-end' }}>
                      <Typography variant='body2SemiBold'>
                        {localisedMessage('continue')}
                      </Typography>
                      <Icons.ArrowRightIcon />
                    </Stack>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      color: globalColours['$global-colour-interactive-primary-default'],
                      '&:hover': {
                        color: globalColours['$global-colour-interactive-primary-hover'],
                      },
                    }}
                    onClick={handleRegisterChild}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        aspectRatio: '1 / 1',
                        width: '48px',
                        border: `solid 1px ${globalColours['$global-colour-interactive-control-default']}`,
                        borderRadius: '100%',
                      }}
                    >
                      <Icons.User
                        sx={{
                          color: globalColours['$global-colour-interactive-control-default'],
                          width: '20px',
                          height: '20px',
                        }}
                      />
                    </Box>
                    <Typography variant='body2SemiBold'>
                      + {localisedMessage('dashboard.home.register.another.child')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Stack>
          )}
        </DashboardLayout>
      )}
    </>
  );
};

Dashboard.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Dashboard.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Dashboard)(
  ({ theme }) => `
`,
);
